import classnames from 'classnames';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import type { Props as ButtonProps } from '#src/atoms/buttons/Button/index.tsx';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Dropdown } from '#src/atoms/dropdowns/Dropdown/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAngleDown } from '#src/atoms/icons/system/iconAngleDown.ts';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { localeConfig, useIntlContext } from '#src/contexts/intl/index.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import type { GenericLanguage } from '#src/lib/i18n.ts';

import { useSetUserSettingsLocale } from './useSetUserSettingsLocale.ts';

type LocaleOption = {
  label: string;
  value: string;
};

const AVAILABLE_LOCALES: LocaleOption[] = (
  Object.keys(localeConfig) as GenericLanguage[]
)
  .sort()
  .map(locale => {
    return {
      label: localeConfig[locale].name,
      value: locale,
    };
  });

const Label = styled(BodyM).attrs({ as: 'label' })`
  display: flex;
  padding: var(--unit) var(--quadruple-unit) var(--unit) var(--double-unit);
  color: var(--c-nd-600);
  &:hover,
  &:focus,
  &:focus-within,
  &.selected {
    background: var(--c-nd-100);
    cursor: pointer;
  }
`;
type Props = {
  renderButton?: (
    props: { content: ReactNode } & Pick<
      ButtonProps,
      'onClick' | 'onMouseEnter' | 'onMouseLeave'
    >
  ) => ReactNode;
};

export const LanguageSwitcher = ({
  renderButton = ({ content, ...props }): ReactNode => (
    <Button {...props} color="tertiary" size="small">
      {content}
    </Button>
  ),
}: Props) => {
  const track = useEvents();
  const { isMobileApp, postMessage } = useIsMobileApp();
  const trackChange = (source?: string) => {
    track('Click LanguageSwitcher', {
      source,
    });
  };
  const { locale, setLocale } = useIntlContext();
  const setUserSettingsLocale = useSetUserSettingsLocale();

  const currentLocale =
    AVAILABLE_LOCALES.find(i => i.value === locale) ||
    AVAILABLE_LOCALES.find(i => i.value === locale.split('-')[0]);
  return (
    <Dropdown
      onOpen={() => trackChange()}
      label={(props): ReactNode =>
        renderButton({
          ...props,
          content: (
            <>
              <span>{currentLocale?.label}</span>
              <FontAwesomeIcon icon={iconAngleDown} />
            </>
          ),
        })
      }
      onChange={e => {
        if (e.target) {
          const { value: newValue } = e.target as HTMLInputElement;
          if (newValue) {
            setUserSettingsLocale(newValue);
            setLocale(newValue);
            trackChange(newValue);

            if (isMobileApp) {
              postMessage('updateLanguagePreference', {
                locale: newValue,
              });
            }
          }
        }
      }}
    >
      <>
        {AVAILABLE_LOCALES.map(lg => (
          <Label
            key={lg.value}
            className={classnames({
              selected: currentLocale?.value === lg.value,
            })}
          >
            {lg.label}
            <input
              type="radio"
              name="language"
              value={lg.value}
              defaultChecked={currentLocale?.value === lg.value}
              className="sr-only"
            />
          </Label>
        ))}
      </>
    </Dropdown>
  );
};
